/**
 * Outil « Application linéaire du plan » (Boîte à outils, prépa).
 *
 * On déplace f(i) et f(j) (les COLONNES de la matrice) : le quadrillage se
 * déforme, le carré unité devient un parallélogramme d'aire |det M|, la
 * lettre F montre si l'orientation est conservée ; un déterminant nul écrase
 * le plan (noyau et image dessinés). Les droites propres sont tracées ; un
 * vecteur v d'essai montre f(v) et dit quand f(v) = λv. Les coefficients se
 * saisissent aussi au clavier (pas de 0,5). En MPSI/PTSI, la réduction (MP/PT)
 * est éteinte au départ. Défi : lire la matrice sur la figure (f(i), f(j)
 * marqués), puis son déterminant. Logique : `linear.ts`.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import {
  Note,
  NumberField,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  ToggleChip,
} from '../../ui/controls';
import { makeFrame, snapPoint, type Frame, type Pt } from '../../ui/frame';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import {
  DragHandle,
  FIGURE_BG_CLASS,
  HaloText,
  INK,
  PlotGrid,
  VectorArrow,
} from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  PRESETS,
  QUIZ_KINDS,
  apply,
  charPolyText,
  collinear,
  det,
  kernelImage,
  makeQuiz,
  num,
  orientation,
  rank,
  spectrum,
  trace,
  type Mat,
  type Quiz,
} from './linear';

const SIZE = 320;
/** fenêtre ±r (4 d'ordinaire ; plus grande si l'on saisit de grands coefficients) */
const vpOf = (r: number) => ({ xMin: -r, xMax: r, yMin: -r, yMax: r });
const FI = 'var(--color-primary-500)';
const FJ = 'var(--color-accent-600)';
const VV = 'var(--color-success-600)';
const EIG = 'var(--color-success-600)';
const KER = 'var(--color-error-600)';
/** la lettre F : asymétrique, elle montre un retournement */
const LETTER: ReadonlyArray<readonly [Pt, Pt]> = [
  [
    { x: 0.4, y: 0.3 },
    { x: 0.4, y: 1.7 },
  ],
  [
    { x: 0.4, y: 1.7 },
    { x: 1.2, y: 1.7 },
  ],
  [
    { x: 0.4, y: 1.05 },
    { x: 1, y: 1.05 },
  ],
];

/** une droite vectorielle de direction d, sur toute la fenêtre */
function lineThrough(frame: Frame, d: Pt) {
  const k = 12;
  return {
    x1: frame.tx(-k * d.x),
    y1: frame.ty(-k * d.y),
    x2: frame.tx(k * d.x),
    y2: frame.ty(k * d.y),
  };
}

function MatrixView({ m }: { m: Mat }) {
  const cell = 'min-w-10 px-1.5 py-0.5 text-center font-semibold tabular-nums';
  return (
    <div
      className="inline-flex items-center gap-2"
      role="img"
      aria-label={`Matrice M : première ligne ${num(m.a)} et ${num(m.b)}, seconde ligne ${num(m.c)} et ${num(m.d)}.`}
      data-testid="lm-matrix"
    >
      <span className="text-ink-900 text-base font-semibold">M =</span>
      <span className="border-ink-700 grid grid-cols-2 rounded-md border-x-2 px-1 text-base">
        <span className={cn(cell, 'text-primary-600')}>{num(m.a)}</span>
        <span className={cn(cell, 'text-accent-700')}>{num(m.b)}</span>
        <span className={cn(cell, 'text-primary-600')}>{num(m.c)}</span>
        <span className={cn(cell, 'text-accent-700')}>{num(m.d)}</span>
      </span>
    </div>
  );
}

/** matrice 2×2 compacte (réponses du défi) */
function MiniMatrix({ m }: { m: Mat }) {
  return (
    <span
      className="border-ink-700 inline-grid grid-cols-2 gap-x-3 rounded-md border-x-2 px-2 tabular-nums"
      aria-hidden="true"
    >
      <span>{num(m.a)}</span>
      <span>{num(m.b)}</span>
      <span>{num(m.c)}</span>
      <span>{num(m.d)}</span>
    </span>
  );
}

const matSpoken = (m: Mat) =>
  `première ligne ${num(m.a)} et ${num(m.b)}, seconde ligne ${num(m.c)} et ${num(m.d)}`;

function Figure({
  m,
  v,
  svgRef,
  svgProps,
  handles,
  showEigen,
  announceDet = true,
  marks = false,
  span = 4,
}: {
  m: Mat;
  v: Pt | null;
  svgRef?: React.RefObject<SVGSVGElement | null>;
  svgProps?: Record<string, unknown>;
  handles?: React.ReactNode;
  showEigen: boolean;
  /** le défi « det M ? » ne doit pas donner la réponse au lecteur d'écran */
  announceDet?: boolean;
  /** sans poignées (défi) : un point et une étiquette au bout de f(i), f(j) */
  marks?: boolean;
  /** demi-largeur de la fenêtre */
  span?: number;
}) {
  const clip = useId();
  const vp = vpOf(span);
  const frame = makeFrame(vp, SIZE);
  const { tx, ty } = frame;
  const fi = { x: m.a, y: m.c };
  const fj = { x: m.b, y: m.d };
  const o = orientation(m);
  const sp = spectrum(m);
  const ki = kernelImage(m);
  const imgLines: Array<{ p: Pt; q: Pt }> = [];
  // assez de lignes pour couvrir la fenêtre : jusqu'aux antécédents de ses coins (plafonné)
  const dt = det(m);
  const reach =
    dt === 0
      ? 16
      : Math.min(
          60,
          Math.ceil(
            Math.max(
              ...[
                [span, span],
                [span, -span],
              ].flatMap(([x, y]) => [
                Math.abs((m.d * x! - m.b * y!) / dt),
                Math.abs((-m.c * x! + m.a * y!) / dt),
              ]),
            ),
          ) + 1,
        );
  const K = Math.max(reach, span + 1);
  for (let k = -K; k <= K; k++) {
    imgLines.push({ p: apply(m, { x: k, y: -K }), q: apply(m, { x: k, y: K }) });
    imgLines.push({ p: apply(m, { x: -K, y: k }), q: apply(m, { x: K, y: k }) });
  }
  const fv = v ? apply(m, v) : null;
  const inView = (p: Pt) => p.x >= vp.xMin && p.x <= vp.xMax && p.y >= vp.yMin && p.y <= vp.yMax;
  const sq = [{ x: 0, y: 0 }, fi, { x: fi.x + fj.x, y: fi.y + fj.y }, fj];
  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className={cn(
        'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role={handles ? 'group' : 'img'}
      aria-label={`Plan transformé par M : f(i) = (${num(m.a)} ; ${num(m.c)}), f(j) = (${num(m.b)} ; ${num(m.d)})${announceDet ? `, déterminant ${num(det(m))}` : ''}.`}
      data-testid="lm-figure"
      {...svgProps}
    >
      <defs>
        <clipPath id={clip}>
          <rect x={0} y={0} width={SIZE} height={SIZE} />
        </clipPath>
      </defs>
      <PlotGrid frame={frame} step={1} labels={false} />
      <g clipPath={`url(#${clip})`}>
        {/* quadrillage image */}
        {imgLines.map(({ p, q }, i) => (
          <line
            key={i}
            x1={tx(p.x)}
            y1={ty(p.y)}
            x2={tx(q.x)}
            y2={ty(q.y)}
            stroke="var(--color-primary-300)"
            strokeWidth={0.9}
            opacity={0.7}
          />
        ))}
        {/* image du carré unité */}
        <polygon
          points={sq.map((p) => `${tx(p.x)},${ty(p.y)}`).join(' ')}
          fill={o === 'indirect' ? 'var(--color-error-500)' : 'var(--color-accent-500)'}
          opacity={0.25}
          data-testid="lm-square"
        />
        {/* lettre F et son image */}
        {LETTER.map(([p, q], i) => (
          <line
            key={`f${i}`}
            x1={tx(p.x)}
            y1={ty(p.y)}
            x2={tx(q.x)}
            y2={ty(q.y)}
            stroke="var(--color-ink-500)"
            strokeWidth={2}
            strokeDasharray="3 3"
          />
        ))}
        {LETTER.map(([p, q], i) => {
          const P = apply(m, p);
          const Q = apply(m, q);
          return (
            <line
              key={`F${i}`}
              x1={tx(P.x)}
              y1={ty(P.y)}
              x2={tx(Q.x)}
              y2={ty(Q.y)}
              stroke="var(--color-ink-800)"
              strokeWidth={2.4}
              strokeLinecap="round"
            />
          );
        })}
        {showEigen &&
          sp.eigen.map(
            (e, i) =>
              e.dir && (
                <line
                  key={`e${i}`}
                  {...lineThrough(frame, e.dir)}
                  stroke={EIG}
                  strokeWidth={1.6}
                  strokeDasharray="7 5"
                  data-testid="lm-eigen"
                />
              ),
          )}
        {ki && (
          <g data-testid="lm-kernel">
            <line
              {...lineThrough(frame, ki.ker)}
              stroke={KER}
              strokeWidth={1.8}
              strokeDasharray="4 4"
            />
            <line
              {...lineThrough(frame, ki.im)}
              stroke={FI}
              strokeWidth={1.8}
              strokeDasharray="10 4"
            />
          </g>
        )}
      </g>
      {/* base de départ */}
      <VectorArrow
        x1={tx(0)}
        y1={ty(0)}
        x2={tx(1)}
        y2={ty(0)}
        color="var(--color-ink-500)"
        width={2}
      />
      <VectorArrow
        x1={tx(0)}
        y1={ty(0)}
        x2={tx(0)}
        y2={ty(1)}
        color="var(--color-ink-500)"
        width={2}
      />
      <VectorArrow
        x1={tx(0)}
        y1={ty(0)}
        x2={tx(fi.x)}
        y2={ty(fi.y)}
        color={FI}
        inset={marks ? 0 : 8}
      />
      <VectorArrow
        x1={tx(0)}
        y1={ty(0)}
        x2={tx(fj.x)}
        y2={ty(fj.y)}
        color={FJ}
        inset={marks ? 0 : 8}
      />
      {marks &&
        (
          [
            [fi, FI, 'f(i)'],
            [fj, FJ, 'f(j)'],
          ] as const
        ).map(([p, color, label]) => (
          <g key={label}>
            <circle cx={tx(p.x)} cy={ty(p.y)} r={4.5} fill={color} />
            <HaloText x={tx(p.x) + 8} y={ty(p.y) - 8} fontSize={13} fontWeight={700} fill={INK}>
              {label}
            </HaloText>
          </g>
        ))}
      {v && fv && (
        <>
          <VectorArrow
            x1={tx(0)}
            y1={ty(0)}
            x2={tx(v.x)}
            y2={ty(v.y)}
            color={VV}
            width={2.4}
            inset={8}
          />
          {(fv.x !== 0 || fv.y !== 0) && (
            <VectorArrow
              x1={tx(0)}
              y1={ty(0)}
              x2={tx(fv.x)}
              y2={ty(fv.y)}
              color={VV}
              width={2.4}
              dashed
            />
          )}
          {(fv.x !== 0 || fv.y !== 0) && inView(fv) && (
            <HaloText x={tx(fv.x) + 6} y={ty(fv.y) - 6} fontSize={13} fontWeight={700} fill={INK}>
              f(v)
            </HaloText>
          )}
        </>
      )}
      {handles}
    </svg>
  );
}

/** les phrases sur le déterminant, le rang, les éléments propres */
function facts(
  m: Mat,
  opts: { eigenShown?: boolean; reduction?: boolean } = {},
): { det: string; rank: string; eigen: string[]; short: string } {
  const dt = det(m);
  const r = rank(m);
  const detLine = `det M = ad − bc = ${num(m.a)} × ${paren(num(m.d))} − ${paren(num(m.b))} × ${paren(num(m.c))} = ${num(dt)}`;
  const rankLine =
    r === 2
      ? `${dt > 0 ? 'det M > 0 : l’orientation est conservée' : 'det M < 0 : l’orientation est retournée (la lettre F est vue dans un miroir)'} ; le carré unité devient un parallélogramme d’aire |det M| = ${num(Math.abs(dt))}. M est inversible (rang 2).`
      : r === 1
        ? 'det M = 0 : le plan est écrasé sur une droite. Rang 1 : le noyau (pointillés rouges) est une droite, l’image (tirets bleus) aussi ; dim Ker f + rg f = 1 + 1 = 2.'
        : 'M = 0 : tout le plan est envoyé sur 0 (rang 0).';
  const sp = spectrum(m);
  const eigen = [
    `${opts.reduction ? 'Réduction (MP/PT) : ' : ''}tr M = ${num(trace(m))}, ${charPolyText(m)}, Δ = (tr M)² − 4 det M = ${num(sp.disc)}.`,
  ];
  if (sp.disc < 0)
    eigen.push(
      'Δ < 0 : pas de valeur propre réelle. Aucune droite n’est stable : f(v) n’est jamais colinéaire à v (v ≠ 0).',
    );
  else if (sp.scalar && sp.eigen[0]!.value === 0)
    eigen.push('M est la matrice nulle : f(v) = 0 pour tout v.');
  else if (sp.scalar)
    eigen.push(
      `M = ${num(sp.eigen[0]!.value)} I : tout vecteur non nul est propre, f(v) = ${num(sp.eigen[0]!.value)}v.`,
    );
  else if (sp.disc === 0)
    eigen.push(
      `Une seule valeur propre λ = ${sp.eigen[0]!.text}, et une seule droite propre : M n’est pas diagonalisable.`,
    );
  else
    eigen.push(
      `Deux valeurs propres λ₁ = ${sp.eigen[0]!.text} et λ₂ = ${sp.eigen[1]!.text} : deux droites propres${opts.eigenShown === false ? '' : ' (en vert)'}, M est diagonalisable.`,
    );
  // annonce courte (lecteur d'écran), à chaque changement de M
  const short = `det M = ${num(dt)}, rang ${r}${
    sp.disc < 0
      ? ', pas de valeur propre réelle'
      : `, valeur${sp.eigen.length > 1 ? 's' : ''} propre${sp.eigen.length > 1 ? 's' : ''} ${sp.eigen.map((e) => e.text).join(' et ')}`
  }.`;
  return { det: detLine, rank: rankLine, eigen, short };
}

const paren = (t: string) => (t.startsWith('−') ? `(${t})` : t);

/** v d'essai par défaut : hors des colonnes des exemples, pour qu'on le voie */
const V0: Pt = { x: -1, y: 1.5 };
/** coefficients au pas de 0,5 (calculs exacts en quarts) */
const half = (x: number) => Math.round(x * 2) / 2;

function Explore({ prepa1 }: { prepa1: boolean }) {
  // MPSI/PTSI : la réduction est au programme de MP/PT, on part d'une transvection
  const start = PRESETS.find((p) => p.id === (prepa1 ? 'shear' : 'diag'))!;
  const [m, setM] = useState<Mat>(start.m);
  const [pid, setPid] = useState<string | null>(start.id);
  const [v, setV] = useState<Pt | null>(V0);
  const [showEigen, setShowEigen] = useState(!prepa1);
  // fenêtre : ±4, agrandie seulement quand on SAISIT un grand coefficient (pas au glisser)
  const [span, setSpan] = useState(4);
  const vp = vpOf(span);
  const svgRef = useRef<SVGSVGElement>(null);
  const frame = makeFrame(vp, SIZE);
  type K = 'i' | 'j' | 'v';
  // v d'abord : sa poignée est dessinée au-dessus, c'est elle qu'on prend
  const ids: K[] = v ? ['v', 'i', 'j'] : ['i', 'j'];
  const place = (p: Pt) => snapPoint(p, vp, 0.5, 0.25);
  const drag = useDrag<K>({
    svgRef,
    frame,
    ids,
    pointOf: (k) => (k === 'i' ? { x: m.a, y: m.c } : k === 'j' ? { x: m.b, y: m.d } : v!),
    onDrag: (k, p) => {
      const q = place(p);
      setPid(null);
      if (k === 'i') setM((x) => ({ ...x, a: q.x, c: q.y }));
      else if (k === 'j') setM((x) => ({ ...x, b: q.x, d: q.y }));
      else setV(q);
    },
    keyStep: 0.5,
  });
  const fv = v ? apply(m, v) : null;
  const f = facts(m, { eigenShown: showEigen, reduction: prepa1 });
  const setCoef = (k: keyof Mat) => (x: number) => {
    setPid(null);
    const next = { ...m, [k]: half(x) };
    setM(next);
    const big = Math.max(Math.abs(next.a), Math.abs(next.b), Math.abs(next.c), Math.abs(next.d));
    setSpan(Math.max(4, Math.ceil(big + 0.5)));
  };
  const out = (p: Pt) => Math.abs(p.x) > vp.xMax || Math.abs(p.y) > vp.yMax;
  // MPSI/PTSI : on parle de droite stable, la réduction viendra en MP/PT
  const proper = prepa1 ? 'la droite ℝv est stable par f' : 'v est un vecteur propre';
  const notProper = prepa1 ? 'la droite ℝv n’est pas stable' : 'v n’est pas propre';
  const vText = !v
    ? null
    : v.x === 0 && v.y === 0
      ? 'v = 0 : prends un vecteur non nul.'
      : collinear(v, fv!)
        ? fv!.x === 0 && fv!.y === 0
          ? `f(v) = 0 : v est dans le noyau${prepa1 ? '' : ' (vecteur propre pour λ = 0)'}.`
          : `f(v) = (${num(fv!.x)} ; ${num(fv!.y)}) est colinéaire à v : ${proper}, f(v) = ${num(Math.abs(v.x) > 0 ? fv!.x / v.x : fv!.y / v.y)} v.${out(fv!) ? ' (f(v) sort du cadre.)' : ''}`
        : `f(v) = (${num(fv!.x)} ; ${num(fv!.y)}) n’est pas colinéaire à v = (${num(v.x)} ; ${num(v.y)}) : ${notProper}.${out(fv!) ? ' (f(v) sort du cadre.)' : ''}`;

  return (
    <div className="space-y-3">
      <div
        role="group"
        aria-label="Exemples"
        className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:thin]"
      >
        {PRESETS.map((p) => (
          <span key={p.id} className="shrink-0">
            <ToggleChip
              on={p.id === pid}
              onClick={() => {
                setPid(p.id);
                setM(p.m);
                setSpan(4);
              }}
            >
              {p.label}
            </ToggleChip>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <MatrixView m={m} />
        <p className="text-ink-700 text-sm">
          {frenchSpaces(`f(i) = (${num(m.a)} ; ${num(m.c)}), f(j) = (${num(m.b)} ; ${num(m.d)})`)}
        </p>
      </div>
      <div className="mx-auto w-full max-w-[26rem]">
        <Figure
          m={m}
          v={v}
          svgRef={svgRef}
          svgProps={drag.svgProps}
          showEigen={showEigen}
          span={span}
          handles={
            <>
              <DragHandle
                x={frame.tx(m.a)}
                y={frame.ty(m.c)}
                color={FI}
                label="f(i)"
                labelDx={m.a > span - 1.5 ? -16 : 16}
                name={`Image f(i) (${num(m.a)} ; ${num(m.c)}), première colonne`}
                active={drag.active === 'i'}
                keyProps={drag.keyProps('i')}
              />
              <DragHandle
                x={frame.tx(m.b)}
                y={frame.ty(m.d)}
                color={FJ}
                label="f(j)"
                labelDx={m.b > span - 1.5 ? -16 : 16}
                name={`Image f(j) (${num(m.b)} ; ${num(m.d)}), seconde colonne`}
                active={drag.active === 'j'}
                keyProps={drag.keyProps('j')}
              />
              {v && (
                <DragHandle
                  x={frame.tx(v.x)}
                  y={frame.ty(v.y)}
                  color={VV}
                  label="v"
                  name={`Vecteur d’essai v (${num(v.x)} ; ${num(v.y)})`}
                  active={drag.active === 'v'}
                  keyProps={drag.keyProps('v')}
                />
              )}
            </>
          }
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <ToggleChip on={showEigen} onClick={() => setShowEigen(!showEigen)}>
          Droites propres
        </ToggleChip>
        <ToggleChip on={!!v} onClick={() => setV(v ? null : V0)}>
          Vecteur d’essai v
        </ToggleChip>
      </div>
      <details className="border-ink-100 rounded-2xl border px-3 py-2">
        <summary className="text-ink-700 min-h-11 cursor-pointer py-2 text-sm font-semibold">
          Saisir les coefficients
        </summary>
        <div className="grid grid-cols-2 gap-2 pt-1" data-testid="lm-inputs">
          <NumberField
            label="a"
            value={m.a}
            onChange={setCoef('a')}
            min={-9}
            max={9}
            step={0.5}
            decimals={1}
            snap={0.5}
          />
          <NumberField
            label="b"
            value={m.b}
            onChange={setCoef('b')}
            min={-9}
            max={9}
            step={0.5}
            decimals={1}
            snap={0.5}
          />
          <NumberField
            label="c"
            value={m.c}
            onChange={setCoef('c')}
            min={-9}
            max={9}
            step={0.5}
            decimals={1}
            snap={0.5}
          />
          <NumberField
            label="d"
            value={m.d}
            onChange={setCoef('d')}
            min={-9}
            max={9}
            step={0.5}
            decimals={1}
            snap={0.5}
          />
        </div>
        <p className="text-ink-500 pt-2 text-xs">
          {frenchSpaces(
            'M = (a b ; c d), rangées comme dans la matrice. Au pas de 0,5 : une valeur saisie est arrondie à la demi-unité.',
          )}
        </p>
      </details>
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(f.short)}
      </p>
      <p className="sr-only" aria-live="polite">
        {vText ? frenchSpaces(vText) : ''}
      </p>
      <div className="space-y-1.5 text-sm" data-testid="lm-facts">
        <p className="text-ink-900 font-semibold">{frenchSpaces(f.det)}</p>
        <p className="text-ink-800">{frenchSpaces(f.rank)}</p>
        {f.eigen.map((t) => (
          <p key={t} className="text-ink-800">
            {frenchSpaces(t)}
          </p>
        ))}
        {vText && (
          <p className="text-ink-800" data-testid="lm-v">
            {frenchSpaces(vText)}
          </p>
        )}
      </div>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          'Déplace f(i) et f(j) : ce sont les colonnes de M. Le quadrillage bleu est l’image du quadrillage, la lettre F en trait plein l’image de la lettre en pointillés. Au clavier : Tab puis les flèches (une demi-unité).',
        )}
      </p>
    </div>
  );
}

function Challenge({ seed }: { seed?: number }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const rounds = QUIZ_KINDS.length;
  const [count, setCount] = useState(1);
  const [q, setQ] = useState<Quiz>(() => makeQuiz(QUIZ_KINDS[0]!, rnd));
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const done = picked !== null && count >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQ(makeQuiz(QUIZ_KINDS[c - 1]!, rnd));
    setPicked(null);
  };
  const f = facts(q.m);

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(
          q.kind === 'matrix'
            ? 'Voici f(i) et f(j), marqués sur la figure. Quelle est la matrice M de f dans la base canonique (i, j) ?'
            : 'Que vaut det M ?',
        )}
      </p>
      {q.kind === 'det' && <MatrixView m={q.m} />}
      <div className="mx-auto w-full max-w-[26rem]">
        <Figure m={q.m} v={null} showEigen={false} announceDet={false} marks />
      </div>
      <div role="group" aria-label="Réponses" className="grid grid-cols-2 gap-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            aria-label={o.m ? `M : ${matSpoken(o.m)}` : undefined}
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-center text-base font-semibold tabular-nums transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            {o.m ? <MiniMatrix m={o.m} /> : frenchSpaces(o.text)}
          </button>
        ))}
      </div>
      {/* zone annoncée toujours présente : le retour est lu dès qu'il apparaît */}
      <div aria-live="polite">
        {chosen && (
          <Note live={false} tone={chosen.correct ? 'success' : 'error'}>
            <span data-testid="lm-feedback">
              {frenchSpaces(
                chosen.correct
                  ? q.kind === 'matrix'
                    ? `Exact : la première colonne est f(i) = (${num(q.m.a)} ; ${num(q.m.c)}), la seconde f(j) = (${num(q.m.b)} ; ${num(q.m.d)}).`
                    : `Exact : ${f.det}. ${f.rank}`
                  : `${chosen.hint ?? ''} ${q.kind === 'det' ? `${f.det}.` : `La bonne réponse : M = ${matSpoken(q.m)}.`}`,
              )}
            </span>
          </Note>
        )}
      </div>
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

export function LinearMapTool({
  seed,
  level = null,
}: {
  /** graine du défi (tests) */
  seed?: number;
  /** niveau de l'élève : en MPSI/PTSI (rang 8), la réduction est éteinte au départ */
  level?: string | null;
}) {
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  const prepa1 = schoolLevelRank(level) === 8;
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore prepa1={prepa1} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} />
      </div>
    </div>
  );
}
